import React from "react";
import { Link } from "react-router-dom";
import { Checkbox, Dropdown } from "flowbite-react";
import { FaArrowRight, FaLock } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../atoms/CustomButton";
import { StatusBadge } from "../../../atoms/StatusBadge";
import NoTranslate from "../../../atoms/NoTranslate";
import { Copy } from "../../../atoms/Copy";

export type Group = {
  group_identification: string;
  group_name: string;
  group_description: string;
  users_count?: number;
};

interface GroupsCardViewProps {
  groups: Array<Group>;
  selectedRowIds: Set<string>;
  toggleRowSelection: (id: string) => void;
  selectAll: boolean;
  onSelectAll: (checked: boolean) => void;
  protectedGroups: string[];
  sortOrder?: "ASC" | "DESC" | "";
  setSortOrder?: (order: "ASC" | "DESC" | "") => void;
}

export const GroupsCardView: React.FC<GroupsCardViewProps> = ({
  groups,
  selectedRowIds,
  toggleRowSelection,
  selectAll,
  onSelectAll,
  protectedGroups,
  sortOrder,
  setSortOrder,
}) => {
  const [t] = useTranslation();

  return (
    <div>
      {groups && groups.length > 0 && (
        <div className="mb-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Checkbox
              onChange={(e) => onSelectAll(e.target.checked)}
              checked={selectAll}
              className="appearance-none checked:bg-blue-600"
            />
            <span className="text-sm font-medium text-gray-700">
              {selectAll
                ? t("dashboard.usersList.deselectAll")
                : t("dashboard.usersList.selectAll")}
            </span>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {setSortOrder && (
              <Dropdown
                label=""
                dismissOnClick
                placement="bottom-end"
                renderTrigger={() => (
                  <CustomButton
                    variant="secondary"
                    title={
                      !sortOrder
                        ? t("dashboard.usersList.item-07")
                        : sortOrder === "ASC"
                        ? t("dashboard.usersList.sortByNameAsc")
                        : t("dashboard.usersList.sortByNameDesc")
                    }
                    icon="chevronDown"
                  />
                )}
              >
                {["ASCENDING", "DESCENDING"].map((item: any) => (
                  <Dropdown.Item
                    key={item}
                    onClick={() =>
                      setSortOrder(
                        item === "ASCENDING"
                          ? "ASC"
                          : item === "DESCENDING"
                          ? "DESC"
                          : ""
                      )
                    }
                  >
                    {item === "ASCENDING"
                      ? t("dashboard.usersList.sortNameAsc")
                      : t("dashboard.usersList.sortNameDesc")}
                  </Dropdown.Item>
                ))}
              </Dropdown>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {groups?.map((groupItem) => {
          const isSelected = selectedRowIds.has(groupItem.group_identification);
          const isProtected = protectedGroups.includes(groupItem.group_name);

          const groupIcon =
            groupItem.group_name.length > 1
              ? `G${groupItem.group_name
                  .charAt(0)
                  .toUpperCase()}${groupItem.group_name
                  .charAt(groupItem.group_name.length - 1)
                  .toUpperCase()}`
              : `G${groupItem.group_name.charAt(0).toUpperCase()}`;

          return (
            <div
              key={groupItem.group_identification}
              className={`bg-white rounded-lg border transition-colors hover:border-gray-300 flex flex-col ${
                isSelected ? "border-blue-500" : "border-gray-200"
              }`}
            >
              <div className="relative p-4 pb-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    onChange={() =>
                      toggleRowSelection(groupItem.group_identification)
                    }
                    checked={isSelected}
                    id={groupItem.group_identification}
                    className="relative z-10 mt-1 appearance-none checked:bg-blue-600 shrink-0"
                  />

                  <div className="shrink-0">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-xs">
                      <NoTranslate>{groupIcon}</NoTranslate>
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/control-panel/groups?group=${groupItem.group_identification}`}
                        title={groupItem.group_name}
                        className="group flex min-w-0 items-center gap-1 text-sm font-semibold text-gray-900 transition-colors hover:text-blue-700 after:absolute after:inset-0 after:content-['']"
                      >
                        <span className="truncate">
                          <NoTranslate>{groupItem.group_name}</NoTranslate>
                        </span>
                        <FaArrowRight className="w-2.5 h-2.5 shrink-0 text-gray-300 transition-colors group-hover:text-blue-700" />
                      </Link>
                      {isProtected && (
                        <FaLock
                          size={12}
                          className="relative z-10 shrink-0 text-gray-400"
                          title={
                            t("dashboard.groupList.protectedTooltip") as string
                          }
                        />
                      )}
                    </div>

                    {groupItem.group_description && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                        <NoTranslate>{groupItem.group_description}</NoTranslate>
                      </p>
                    )}

                    {isProtected && (
                      <StatusBadge color="warning" className="mt-2 inline-flex">
                        {t("dashboard.groupList.protected")}
                      </StatusBadge>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 pt-0">
                <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                  <dt className="text-gray-500">
                    {t("dashboard.usersList.item-15")}
                  </dt>
                  <dd className="flex min-w-0 items-center gap-1.5 font-mono text-gray-700">
                    <span className="truncate">
                      <NoTranslate>
                        {groupItem.group_identification?.substring(0, 5) +
                          "..." +
                          groupItem.group_identification?.slice(
                            groupItem.group_identification.length - 5
                          )}
                      </NoTranslate>
                    </span>
                    <Copy
                      target={groupItem.group_identification}
                      iconOnly
                      title={t("dashboard.usersList.copyId") as string}
                      className="rounded p-1 transition-colors hover:bg-gray-100"
                      iconClassName="h-3 w-3"
                    />
                  </dd>

                  {groupItem.users_count !== undefined && (
                    <>
                      <dt className="text-gray-500">
                        {t("dashboard.groupList.userCountShort")}
                      </dt>
                      <dd className="tabular-nums text-gray-700">
                        {groupItem.users_count}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
